<template>
  <grid-container-fi class="jump-mark-tabs-fi">
    <tab-navigation-fi
      ref="jumpMarkTabNavi"
      class="jump-mark-tabs-fi__tabs"
      :tab-navigation="tabNavigation"
      theme="jump-marks"
      enable-scroll-if-out-of-bounds-only
      line-hidden
      @change="tabChanged"
    />
  </grid-container-fi>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import TabNavigationFi from '@/components/tab-navigation-fi/TabNavigationFi.vue';
import { useSmoothScroll } from '@/composables/smoothScroll';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({ name: 'JumpMarkTabsFi' });

const navigationStore = useNavigationStore();
const { smoothScrollToElement } = useSmoothScroll();

const spacing = 30;
const scrollDuration = 500;

let tabChangeByScrollingEnabled = true;
let scrollToJumpMarkEnabled = true;

const jumpMarkTabNavi = useTemplateRef('jumpMarkTabNavi');

const jumpMarks = computed(() => navigationStore.jumpMarks);
const activeJumpMark = computed(() => navigationStore.activeJumpMark);
const tabNavigation = computed(() =>
  jumpMarks.value.map((jm, index) => ({
    id: index + 1,
    text: jm.title,
    offsetTop: jm.offsetTop,
  })),
);

function scrollToJumpMark(element: any) {
  smoothScrollToElement(element, {
    duration: scrollDuration,
    offset: spacing,
  });
}

function onJumpMarkClicked(element: any) {
  tabChangeByScrollingEnabled = false;
  scrollToJumpMark(element);
  setTimeout(() => {
    tabChangeByScrollingEnabled = true;
  }, scrollDuration);
}

function tabChanged() {
  const offsetValue = tabNavigation.value.find(
    (t) => t.id === jumpMarkTabNavi.value?.activeId,
  )?.offsetTop;
  const targetJumpMark = jumpMarks.value.find((jm) => jm.offsetTop === offsetValue);

  if (!scrollToJumpMarkEnabled || !targetJumpMark) {
    return;
  }

  onJumpMarkClicked(targetJumpMark.element);
}

async function checkAnchors() {
  if (IS_SSR) {
    return;
  }

  const currentAnchor = window.location?.hash?.slice(1);

  if (!currentAnchor) {
    return;
  }

  await nextTick(); // wait for header-height to potentially update
  await nextTick(); // we need to wait twice, since header-height-fi itself waits for nextTick :(

  const targetJumpMark = jumpMarks.value.find((jm) => jm.anchorName === currentAnchor);

  if (!targetJumpMark?.element) {
    return;
  }

  scrollToJumpMark(targetJumpMark.element);

  // Reset anchor after scroll to is triggered to prevent multiple 'scrollToJumpMark' calls
  const currentSearchParams = window.location.search;
  window.history.pushState(null, '', `${currentSearchParams}`);
}

watch(
  jumpMarks,
  (items) => {
    if (!items?.length) {
      return;
    }

    checkAnchors();
  },
  { deep: true, immediate: true },
);

watch(
  activeJumpMark,
  (item) => {
    if (!tabChangeByScrollingEnabled) {
      return;
    }

    scrollToJumpMarkEnabled = false;
    if (item.offsetTop) {
      const relatedTab = tabNavigation.value.find((t) => t.offsetTop === item.offsetTop);
      if (relatedTab) {
        jumpMarkTabNavi.value?.changeTab(relatedTab.id, relatedTab.id - 1);
      }
    } else {
      jumpMarkTabNavi.value?.changeTab(0, 0);
    }

    setTimeout(() => {
      scrollToJumpMarkEnabled = true;
    }, scrollDuration);
  },
  { deep: true },
);
</script>

<style scoped src="./jump-mark-tabs-fi" />
